import React from 'react';
import { Save } from 'lucide-react';
import { Input } from '../ui/arc/input';
import { Checkbox } from '../ui/arc/checkbox';
import { Button } from '../ui/arc/button';
import { Badge } from '../ui/arc/badge';

// Variables globales (pestaña Costos & BI → Config) — rediseño 10/10/2026.
// Se mantiene el botón "Guardar cambios" (no autoguardado): `handleUpdateSettings` hace un
// upsert de la fila COMPLETA de cost_settings, y guardar a cada tecla un % de IVA o la
// cotización a medio escribir impactaría al toque en la Calculadora y en los mensajes de
// WhatsApp. Lo nuevo es que se avisa si hay cambios sin guardar.

export interface ConfigSettings {
    id: number;
    usd_rate: number;
    eur_rate?: number;
    iva_percent: number;
    acrylic_waste_percent: number;
    use_epic_week_templates?: boolean;
    local_address?: string | null;
    local_hours?: string | null;
    cr_community_link?: string | null;
}

function Section({ title, help, children, tone }: { title: string; help: string; children: React.ReactNode; tone?: 'alert' }) {
    return (
        <section className={
            'grid gap-4 rounded-[var(--radius-surface)] border p-5 '
            + (tone === 'alert'
                ? 'border-[color-mix(in_srgb,var(--danger)_45%,transparent)] bg-[color-mix(in_srgb,var(--danger)_5%,var(--card))]'
                : 'border-line bg-card')
        }>
            <div>
                <h3 className="text-[length:var(--text-base)] font-semibold text-ink">{title}</h3>
                <p className="mt-0.5 text-[length:var(--text-sm)] text-soft">{help}</p>
            </div>
            {children}
        </section>
    );
}

/** Campo numérico con la unidad visible en la etiqueta (el Input de uiarc no trae sufijo). */
function NumField({ label, unit, value, onChange, description }: {
    label: string; unit: string; value: number | undefined; onChange: (n: number) => void; description?: string;
}) {
    return (
        <Input label={`${label} (${unit})`} type="number" inputMode="decimal" description={description}
            value={value ?? ''} onChange={e => onChange(Number(e.target.value))} className="tabular-nums" />
    );
}

export function ConfigPanel({
    settings, setSettings, onSave, saveMsg,
}: {
    settings: ConfigSettings;
    setSettings: (s: any) => void;
    onSave: () => void;
    saveMsg: string;
}) {
    // "Hay cambios sin guardar": se compara contra lo último cargado/guardado.
    const snap = (s: ConfigSettings) => JSON.stringify({
        u: s.usd_rate, e: s.eur_rate ?? 1800, i: s.iva_percent, w: s.acrylic_waste_percent,
        ew: !!s.use_epic_week_templates, cr: s.cr_community_link || '', a: s.local_address || '', h: s.local_hours || '',
    });
    const [base, setBase] = React.useState(() => snap(settings));
    React.useEffect(() => { if (saveMsg.startsWith('Guardado')) setBase(snap(settings)); }, [saveMsg]); // eslint-disable-line react-hooks/exhaustive-deps
    const dirty = snap(settings) !== base;
    const set = (patch: Partial<ConfigSettings>) => setSettings({ ...settings, ...patch });
    const isErr = saveMsg.startsWith('Error');

    return (
        <div className="tw w-full min-w-0 pb-24">
            <header className="mb-5">
                <h2 className="text-[length:var(--text-xl)] font-semibold leading-tight tracking-[var(--tracking-display)] text-ink">Configuración</h2>
                <p className="mt-1 max-w-[72ch] text-[length:var(--text-sm)] text-soft">
                    Variables que usan los costos, la calculadora y los mensajes automáticos de WhatsApp.
                </p>
            </header>

            <div className="grid max-w-[960px] gap-4">
                <Section title="Cotizaciones" help="Pesos por cada unidad de moneda. Convierten a ARS los insumos cargados en USD o EUR.">
                    <div className="grid gap-4 sm:grid-cols-2">
                        <NumField label="Dólar (blue/oficial)" unit="$ / USD" value={settings.usd_rate} onChange={n => set({ usd_rate: n })} />
                        <NumField label="Euro" unit="$ / EUR" value={settings.eur_rate ?? 1800} onChange={n => set({ eur_rate: n })} />
                    </div>
                </Section>

                <Section title="Impuestos y costeo" help="Se aplican en la Calculadora de rentabilidad.">
                    <div className="grid gap-4 sm:grid-cols-2">
                        <NumField label="IVA" unit="%" value={settings.iva_percent} onChange={n => set({ iva_percent: n })}
                            description="Se suma al precio del acrílico y al PVP final." />
                        <NumField label="Desperdicio de acrílico" unit="%" value={settings.acrylic_waste_percent} onChange={n => set({ acrylic_waste_percent: n })}
                            description="Recorte que se pierde al cortar; agranda el área usada." />
                    </div>
                </Section>

                <Section title="Epic Week" tone={settings.use_epic_week_templates ? 'alert' : undefined}
                    help="Cambia las plantillas de confirmación de pedido durante la semana de promo.">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <Checkbox checked={!!settings.use_epic_week_templates}
                            onCheckedChange={v => set({ use_epic_week_templates: v === true })}
                            label="Usar plantillas de Epic Week"
                            description="Las confirmaciones salen con order_confirmation_epic_week_v2 y cash_order_confirmation_epic_week_v2. Rige del 26/10 0:00 al 01/11 24:00 (epic_week_starts_at / epic_week_ends_at): arranca y se apaga solo." />
                        {settings.use_epic_week_templates && <Badge tone="danger" size="sm">Activo</Badge>}
                    </div>
                </Section>

                <Section title="Comunidad Círculo Rojo" help="Link al grupo/canal de WhatsApp. Va como {{3}} en los templates circulo_rojo_credencial_*.">
                    <Input label="Link de la comunidad" type="url" placeholder="https://chat.whatsapp.com/…"
                        value={settings.cr_community_link || ''} onChange={e => set({ cr_community_link: e.target.value || null })}
                        className="font-mono" />
                </Section>

                <Section title="Local físico" help="Se le manda al cliente cuando confirma que retira en el local (respuesta automática).">
                    <div className="grid gap-4 sm:grid-cols-2">
                        <Input label="Dirección" placeholder="Av. Ejemplo 1234, CABA"
                            value={settings.local_address || ''} onChange={e => set({ local_address: e.target.value })} />
                        <Input label="Horarios" placeholder="Lunes a sábado de 10 a 19 hs"
                            value={settings.local_hours || ''} onChange={e => set({ local_hours: e.target.value })} />
                    </div>
                </Section>
            </div>

            {/* Barra de guardado fija abajo: siempre a mano, y dice si hay algo pendiente. */}
            <div className="sticky bottom-0 z-10 mt-4 max-w-[960px]">
                <div className="flex flex-wrap items-center justify-end gap-3 rounded-[var(--radius-surface)] border border-line bg-card px-4 py-3 shadow-sm">
                    <span className={'mr-auto text-[length:var(--text-sm)] ' + (isErr ? 'text-[var(--danger)]' : saveMsg ? 'text-[var(--success)]' : dirty ? 'text-[var(--warning)]' : 'text-faint')}>
                        {saveMsg || (dirty ? 'Hay cambios sin guardar' : 'Todo guardado')}
                    </span>
                    <Button onClick={onSave} disabled={!dirty && !isErr}><Save size={16} /> Guardar cambios</Button>
                </div>
            </div>
        </div>
    );
}
